import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Typography, useTheme } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import { onFlatMapStyleReady } from '../utils/mapStyle';
import 'mapbox-gl/dist/mapbox-gl.css';

export type IncidentLocation = { latitude: number; longitude: number; reportedAt: string };
export const validIncidentLocation = (location?: IncidentLocation): location is IncidentLocation => !!location &&
  Number.isFinite(location.latitude) && Number.isFinite(location.longitude) &&
  Math.abs(location.latitude) <= 90 && Math.abs(location.longitude) <= 180 &&
  !(location.latitude === 0 && location.longitude === 0);

export default function IncidentLocationMap({ location }: { location: IncidentLocation }) {
  const container = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { latitude, longitude } = location;
  const valid = validIncidentLocation(location);
  useEffect(() => {
    setError('');
    if (!valid || !container.current) return;
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) { setError('The map is unavailable. The recorded coordinates are shown below.'); return; }
    mapboxgl.accessToken = token;
    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({ container: container.current, style: mode === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12', center: [longitude, latitude], zoom: 15 });
    } catch { setError('The map could not start. The recorded coordinates are shown below.'); return; }
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    const removeListener = onFlatMapStyleReady(map, () => {
      map.addSource('incident-location', { type: 'geojson', data: { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [longitude, latitude] } } });
      map.addLayer({ id: 'incident-pin', type: 'circle', source: 'incident-location', paint: { 'circle-radius': 10, 'circle-color': '#e65100', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } });
    });
    map.on('error', () => setError('Some map tiles could not load. The recorded coordinates remain available.'));
    const resize = new ResizeObserver(() => map.resize());
    resize.observe(container.current);
    return () => { resize.disconnect(); removeListener(); map.remove(); };
  }, [latitude, longitude, valid, mode]);
  return <Box>
    {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
    <Box ref={container} role="region" aria-label="Last recorded incident location map" sx={{ height: { xs: 260, sm: 340 }, width: '100%', borderRadius: 2, overflow: 'hidden' }} />
    <Typography variant="body2" sx={{ mt: 1 }}>Recorded location: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</Typography>
    <Typography variant="caption" color="text.secondary">Location received: {new Date(location.reportedAt).toLocaleString()}. This is the recorded incident location, not the tracker’s current position.</Typography>
  </Box>;
}
